/**
 * Diffie-Hellman based Private Set Intersection (PSI) over the ristretto255
 * prime-order group.
 *
 * Two parties learn ONLY the intersection of their sets; neither learns the
 * other's non-matching elements. Used for zero-knowledge scheduling: hash free
 * time slots to curve points, blind with a secret scalar, exchange, cross-blind,
 * and match the doubly-blinded points.
 *
 * Security rests on:
 *  - the hash-to-curve being a random oracle (a blinded point H(x)^a reveals
 *    nothing about x without the secret scalar a), and
 *  - blinding commuting: (H(x)^a)^b == (H(x)^b)^a, so equal inputs collide only
 *    after both parties have applied their scalar.
 */
import { ristretto255 } from "@noble/curves/ed25519";
import { sha512 } from "@noble/hashes/sha512";
import { randomBytes } from "@noble/ciphers/webcrypto";

const Point = ristretto255.Point;
const ORDER: bigint = Point.Fn.ORDER;

/** A secret blinding scalar in [1, ORDER-1]. */
export function randomScalar(): bigint {
  for (;;) {
    const bytes = randomBytes(64);
    let n = 0n;
    for (const byte of bytes) n = (n << 8n) | BigInt(byte);
    const s = ((n % ORDER) + ORDER) % ORDER;
    if (s !== 0n) return s;
  }
}

/** Map an element (e.g. a slot id) to a curve point via hash-to-curve (internal). */
function hashToPoint(element: string) {
  return Point.hashToCurve(sha512(new TextEncoder().encode(element)));
}

/** The hash-to-curve point of an element, as hex. Exposed for verification/analysis. */
export function hashHex(element: string): string {
  return hashToPoint(element).toHex();
}

/** A single element blinded by a scalar, as hex: H(element)^scalar. */
export function blindElement(element: string, scalar: bigint): string {
  return hashToPoint(element).multiply(scalar).toHex();
}

/** Round 1 (initiator): blind own set with secret scalar `a`. Order is preserved. */
export function blind(set: string[], a: bigint): string[] {
  return set.map((s) => hashToPoint(s).multiply(a).toHex());
}

export interface PsiRound2 {
  /** The initiator's blinded points, re-blinded with `b` (order matches the input). */
  doubleBlinded: string[];
  /** The responder's own set, blinded with `b`. */
  blindedReply: string[];
}

/**
 * Round 2 (responder): receives the initiator's blinded set, double-blinds it
 * with secret scalar `b`, and returns its own set blinded with `b`.
 */
export function respond(initiatorBlinded: string[], ownSet: string[], b: bigint): PsiRound2 {
  const doubleBlinded = initiatorBlinded.map((h) => Point.fromHex(h).multiply(b).toHex());
  const blindedReply = blind(ownSet, b);
  return { doubleBlinded, blindedReply };
}

/**
 * Round 3 (initiator): double-blind the responder's set with `a`, then intersect
 * with the doubly-blinded copy of the initiator's own set. Returns ONLY the
 * elements common to both sets (as the initiator's original slot ids). The
 * responder's non-matching elements are never recoverable here.
 */
export function finalize(
  round2: PsiRound2,
  a: bigint,
  ownSet: string[],
): string[] {
  const mine = new Map<string, string>(); // H(x)^(ab) -> original slot x
  round2.doubleBlinded.forEach((h, i) => {
    const slot = ownSet[i];
    if (slot !== undefined) mine.set(h, slot);
  });
  const theirsDoubleBlinded = round2.blindedReply.map((h) => Point.fromHex(h).multiply(a).toHex());
  const intersection: string[] = [];
  for (const h of theirsDoubleBlinded) {
    const slot = mine.get(h);
    if (slot !== undefined) intersection.push(slot);
  }
  return intersection;
}
